import { useEffect, useReducer, useState, useCallback, useRef } from "react";
import type { U2000FormSBusinessHandlers, U2000FormStateHandlers, U2000FormStateHandlersReturn } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";

import U2000DefaultForm from "../../u2000Core/components/formControls/U2000DefaultForm";

import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000GetHandlers } from "../../u2000Core/hooks/handler/U2000UseGetHandlers";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import type { U2000SearchPayload, U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import U2000Tab from "../../u2000Core/components/tab/U2000Tab";
import { useU2000SaveHandlers } from "../../u2000Core/hooks/handler/U2000UseSaveHandlers";
import u2000FormState, { U2000FormStateEnumEditMode, U2000FormStateEnumState, type U2000FormState } from "../../u2000Core/reducer/formReducer/U2000FormState";
import { u2000IsFormRowDirty } from "../../u2000Core/tools/u2000FormDirtyUtils";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import U2000AuditPanelComponent from "../../u2000Core/components/audit/U2000AuditPanelComponent";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import {
  doCreateData,
  doDeleteData,
  doFetchHistoriqueCadastral,
  doGetAuditsData,
  doGetChainImportJob,
  doGetData,
  doGetCadastralHistoryDocumentUrl,
  doGetSiteAppliSFoncierVersion,
  doGetU2200Job,
  doMintPlanUploadToken,
  doFetchAnnotatedLotChainIndexPdf,
  doFetchAnnotatedLotChainIndexPdfDryRun,
  doImportChainAnnotationsApply,
  doImportChainAnnotationsDryRun,
  doReconstituteChainFromPdf,
  doSearch,
  doUpdateData,
  luHandleAppiError,
  u3141ExtractChainPdfAlertesFromJob,
  u3141ExtractChainPdfLotsRegeneresFromJob,
  U3141EmptyLandRegister,
  u3141MasterLotChainToApiPayload,
  u3141ParseU2200JobResponse,
  u3141ReadChainImportProgress,
  type U3141ChainImportProgress,
  type U3141ChainPdfAlerte,
  type U3141ChainPdfLotRegenere,
  type U3141CreatePrefillState,
  type U3141AnnotatedLotChainIndexMergeDryRunResponse,
  type U3141ImportChainAnnotationsDryRunLot,
  type U3141LandRegisterLocationState,
  type U3141MasterLotChain,
} from "../U3141Service";
import { useU3141Context } from "../U3141Context";
import { NS, U3141ROLES } from "../U3141";
import U2000BasicDialog from "../../u2000Core/components/dialog/U2000BasicDialog";
import U2000HtmlDisplayDialog from "../../u2000Core/components/dialog/U2000HtmlDisplayDialog";
import U2000YesNoDialog from "../../u2000Core/components/dialog/U2000YesNoDialog";
import U3141LandRegisterDeleteDlg from "./common/U3141LandRegisterDeleteDlg";
import { U3141MasterLotChainFormToolBar } from "./form/u3141MasterLotChain/U3141MasterLotChainFormToolBar";
import U3141MasterLotChainBody from "./form/u3141MasterLotChain/U3141MasterLotChainBody";
import U3141MasterChildLotChainBody from "./form/u3141MasterLotChain/u3141ChildLotChainTab/U3141MasterChildLotChainBody";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import {
  u3141DetectSiteAppliPlatform,
  u3141ListSiteAppliDownloadChoices,
  u3141ResolveSiteAppliArtifact,
  type U3141SiteAppliArtifact,
} from "../tools/u3141ResolveSiteAppliArtifact";
import { u3141GetHistoriqueCadastralDocumentId } from "../tools/u3141GetHistoriqueCadastralDocumentId";
import { useU3141UseFetchHistoriqueCadastralHandlers } from "./form/u3141MasterLotChain/U3141UseFetchHistoriqueCadastralHandlers";
import { u3141NormalizeHistoriqueCadastralHtml } from "./form/u3141MasterLotChain/U3141HistoriqueCadastralHtml";
import { useU3141UseDownloadHistoriqueCadastralHtmlHandlers } from "./form/u3141MasterLotChain/U3141UseDownloadHistoriqueCadastralHtmlHandlers";
import { useU3141HistoriqueCadastralNotification } from "./form/u3141MasterLotChain/U3141UseHistoriqueCadastralNotification";
import { useU3141UseReconstituteChainFromPdfHandlers } from "./form/u3141MasterLotChain/U3141UseReconstituteChainFromPdfHandlers";
import { useU3141ReconstituteChainFromPdfNotification } from "./form/u3141MasterLotChain/U3141UseReconstituteChainFromPdfNotification";
import { U3141ReconstituteChainPdfDialog } from "./form/u3141MasterLotChain/U3141ReconstituteChainPdfDialog";
import { U3141ImportChainAnnotationsDialog } from "./form/u3141MasterLotChain/U3141ImportChainAnnotationsDialog";
import { U3141GenerateAnnotatedLotChainDialog } from "./form/u3141MasterLotChain/U3141GenerateAnnotatedLotChainDialog";
import { useU3141ImportChainAnnotationsHandlers } from "./form/u3141MasterLotChain/useU3141ImportChainAnnotationsHandlers";
import { useU3141GenerateAnnotatedLotChainHandlers } from "./form/u3141MasterLotChain/useU3141GenerateAnnotatedLotChainHandlers";
import { useU3141ImportChainAnnotationsNotification } from "./form/u3141MasterLotChain/useU3141ImportChainAnnotationsNotification";
import { useU3141AnnotatedLotChainNotification } from "./form/u3141MasterLotChain/u3141ChildLotChainTab/U3141UseAnnotatedLotChainNotification";
import {
  U3141ChainImportProgressDialog,
  U3141_ANNOTATED_LOT_CHAIN_MERGE_STEPS,
  U3141_CHAIN_ANNOTATIONS_IMPORT_STEPS,
  U3141_HISTORIQUE_PROGRESS_STEPS,
} from "./form/u3141MasterLotChain/U3141ChainImportProgressDialog";
import { u3141CanLaunchAnnotatedLotChainMergeFromDryRun } from "../tools/u3141AnnotatedLotChainIndexMergeHelpers";
import { u3141ExtractImportChainAnnotationsSummaryFromJob } from "../tools/u3141ImportChainAnnotationsHelpers";
import { U3141ChainPdfAlertesDialog } from "./form/u3141MasterLotChain/U3141ChainPdfAlertesDialog";
import { u3141IsClientRequestUuid, useU3141MintPlanUploadTokenHandlers } from "./form/u3141MasterLotChain/U3141UseMintPlanUploadTokenHandlers";
import { useU3141PlanUploadProgressNotification } from "./form/u3141MasterLotChain/U3141UsePlanUploadProgressNotification";
import { U3141PlanUploadProgressDialog } from "./form/u3141MasterLotChain/U3141PlanUploadProgressDialog";
import {
  u3141ApplyPlanUploadProgressEvent,
  u3141InitialPlanUploadProgressState,
} from "../tools/u3141PlanUploadProgressState";
import type { U3141PlanUploadProgressWsMessage } from "../../u2000Core/types/U2000WebSocketMessages";
import { useU3141GetSiteAppliSFoncierVersionHandlers } from "./form/u3141MasterLotChain/U3141UseGetSiteAppliSFoncierVersionHandlers";
import { useU3141OpenDocumentPdfFileHandlers } from "./form/u3141ChildLotChain/U3141UseOpenDocumentPdfFileHandlers";
import {
  u3141DecodeDocumentPdfFileAsText,
  u3141InferDocumentMime,
  u3141IsHtmlMime,
  u3141OpenDocumentPdfFileInNewTab,
} from "../tools/u3141OpenDocumentPdfFile";

/** Retourne true si l'URL pointe vers un document HTML (extension .html ou .htm dans le path). */
function isHtmlDocumentUrl(url: string): boolean {
  try {
    const pathname = new URL(url).pathname;
    const decoded = decodeURIComponent(pathname);
    return /\.html?$/i.test(decoded);
  } catch {
    return false;
  }
}

// Interface à placer en dehors du composant pour éviter l'erreur de modificateur
export interface U3141MasterLotChainFormExtendedHandlers extends U2000FormStateHandlersReturn<U3141MasterLotChain> {
  addNewMasterLotChain: () => void;
  modifyMasterLotChain: () => void;
  deleteMasterLotChain: () => void;
  fetchHistoriqueCadastral: (masterLotChainId: string) => Promise<void>;
  downloadHistoriqueCadastralHtml: (masterLotChainId: string) => Promise<void>;
  mintPlanUploadToken: (masterLotChainId: string) => Promise<void>;
  /** Télécharge le ZIP siteAppliSFoncier (BL-0456). */
  downloadSiteAppliSFoncier: () => Promise<void>;
  /** Ouvre le dialogue d'import PDF de chaîne (BL-0562). */
  openReconstituteChainFromPdf: () => void;
  /** Ouvre le wizard import annotations seules (BL-0700). */
  openImportChainAnnotations: () => void;
  /** Ouvre le wizard dry-run génération chaîne annotée (BL-0704). */
  openGenerateAnnotatedLotChain: (anchorChildLotChainId: string) => void;
  /** Retrait optimiste du doc chaîne annotée sur le nœud graphe (enregistré par le graphe). */
  beforeAnnotatedMergeJobLaunch?: (anchorChildLotChainId: string) => void;
  /** Refetch du lot maître (après sauvegarde/reset des positions du graphe). */
  refetchMasterLotChain?: () => Promise<void>;
  /** Recharge lotChainGraph si le dialogue graphe est ouvert (BL-0700, BL-0709 historique, etc.). */
  refreshLotChainGraphIfOpen?: (options?: { relayoutStructure?: boolean }) => Promise<void>;
  /** Cooldown après lancement scraping (parité menu graphe BL-0702). */
  fetchHistoriqueCadastralCooldown?: boolean;
}

const U3141LandRegisterForm: React.FC = () => {
  // Parameters
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  // Form state and reducer for U3141LandRegister
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  // manage active tab state
  const [activeTab, setActiveTab] = useState<string | null>(null);

  // Popup confirmation avant fetch historique cadastral (frais 1$, notification, mise à jour auto)
  const [isFetchHistoriqueCadastralConfirmOpen, setFetchHistoriqueCadastralConfirmOpen] = useState(false);
  const [pendingFetchHistoriqueCadastralId, setPendingFetchHistoriqueCadastralId] = useState<string | null>(null);
  const [isFetchHistoriqueCadastralCooldown, setFetchHistoriqueCadastralCooldown] = useState(false);

  // Dialog affichage HTML historique cadastral (75 % écran)
  const [isHistoriqueCadastralHtmlOpen, setHistoriqueCadastralHtmlOpen] = useState(false);
  const [historiqueCadastralHtmlContentUrl, setHistoriqueCadastralHtmlContentUrl] = useState<string | null>(null);
  /** HTML normalisé (srcdoc) : évite les sous-blocs ouverts par défaut sans dépendre du backend. */
  const [historiqueCadastralHtmlSrcDoc, setHistoriqueCadastralHtmlSrcDoc] = useState<string | null>(null);

  // Saisie de l'identifiant de requête (UUID) avant mint — aligné x-client-request-uuid de l'app tiers
  const [planUploadTokenRequest, setPlanUploadTokenRequest] = useState<{ masterLotChainId: string } | null>(null);
  const [planUploadClientRequestUuidDraft, setPlanUploadClientRequestUuidDraft] = useState("");

  // Dialogue jeton d'upload de plans (ZIP) — résultat après mint
  const [planUploadTokenDialog, setPlanUploadTokenDialog] = useState<{
    accessToken: string;
    expiresAt: string;
    clientRequestUuid: string;
  } | null>(null);

  /** Corrélation WS plan upload (BL-0603) — conservée après fermeture manuelle du dialogue jeton. */
  const [planUploadWatch, setPlanUploadWatch] = useState<{
    clientRequestUuid: string;
    masterLotChainId: string;
  } | null>(null);
  const [planUploadProgress, setPlanUploadProgress] = useState(u3141InitialPlanUploadProgressState);

  // Dialogue choix OS pour téléchargement siteAppliSFoncier (BL-0456)
  const [siteAppliOsChoices, setSiteAppliOsChoices] = useState<{
    macos?: U3141SiteAppliArtifact;
    windows?: U3141SiteAppliArtifact;
  } | null>(null);

  // Import PDF de chaîne (BL-0562)
  const [isReconstituteChainPdfOpen, setReconstituteChainPdfOpen] = useState(false);
  const [isReconstituteChainPdfSubmitting, setReconstituteChainPdfSubmitting] = useState(false);
  const pendingReconstituteJobIdRef = useRef<string | null>(null);
  const chainImportFinishedRef = useRef(false);
  const [chainImportJobId, setChainImportJobId] = useState<string | null>(null);
  const [chainImportProgress, setChainImportProgress] = useState<U3141ChainImportProgress>({ percent: 0, step: "preparation" });
  const [chainImportError, setChainImportError] = useState<string | null>(null);
  const historiqueFinishedRef = useRef(false);
  const historiqueErrorRef = useRef<string | null>(null);
  const [historiqueJobId, setHistoriqueJobId] = useState<string | null>(null);
  const [historiqueProgress, setHistoriqueProgress] = useState<U3141ChainImportProgress>({ percent: 0, step: "preparation" });
  const [historiqueError, setHistoriqueError] = useState<string | null>(null);
  historiqueErrorRef.current = historiqueError;
  const [chainPdfAlertesOpen, setChainPdfAlertesOpen] = useState(false);
  const [chainPdfAlertes, setChainPdfAlertes] = useState<U3141ChainPdfAlerte[]>([]);
  const [chainPdfAlertesFicheCount, setChainPdfAlertesFicheCount] = useState<number | undefined>(undefined);

  // Import annotations chaîne (BL-0700)
  const [isImportChainAnnotationsOpen, setImportChainAnnotationsOpen] = useState(false);
  const [importChainAnnotationsDryRunLoading, setImportChainAnnotationsDryRunLoading] = useState(false);
  const [importChainAnnotationsApplyLoading, setImportChainAnnotationsApplyLoading] = useState(false);
  const [importChainAnnotationsDryRunLots, setImportChainAnnotationsDryRunLots] = useState<
    U3141ImportChainAnnotationsDryRunLot[] | null
  >(null);
  const [importChainAnnotationsSourceDocumentId, setImportChainAnnotationsSourceDocumentId] = useState<string | null>(
    null,
  );
  const importChainAnnotationsFinishedRef = useRef(false);
  const pendingImportChainAnnotationsJobIdRef = useRef<string | null>(null);
  const [importChainAnnotationsJobId, setImportChainAnnotationsJobId] = useState<string | null>(null);
  const [importChainAnnotationsProgress, setImportChainAnnotationsProgress] = useState<U3141ChainImportProgress>({
    percent: 0,
    step: "preparation",
  });
  const [importChainAnnotationsError, setImportChainAnnotationsError] = useState<string | null>(null);

  // Génération chaîne annotée dry-run + job (BL-0704)
  const [isGenerateAnnotatedChainOpen, setGenerateAnnotatedChainOpen] = useState(false);
  const [generateAnnotatedAnchorChildId, setGenerateAnnotatedAnchorChildId] = useState<string | null>(null);
  const [generateAnnotatedDryRunResult, setGenerateAnnotatedDryRunResult] =
    useState<U3141AnnotatedLotChainIndexMergeDryRunResponse | null>(null);
  const [generateAnnotatedDryRunLoading, setGenerateAnnotatedDryRunLoading] = useState(false);
  const [generateAnnotatedLaunchLoading, setGenerateAnnotatedLaunchLoading] = useState(false);
  const annotatedMergeFinishedRef = useRef(false);
  const pendingAnnotatedMergeJobIdRef = useRef<string | null>(null);
  const [annotatedMergeJobId, setAnnotatedMergeJobId] = useState<string | null>(null);
  const [annotatedMergeProgress, setAnnotatedMergeProgress] = useState<U3141ChainImportProgress>({
    percent: 0,
    step: "preparation",
  });
  const [annotatedMergeError, setAnnotatedMergeError] = useState<string | null>(null);

  // navigate et location
  const location = useLocation();
  const navigate = useNavigate();

  // Translation
  const { t } = useTranslation();
  const toast = useToast();

  // APA Axios Client
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  // LU Handler,  Search Handlers, Grid Handlers from Context
  const { luStateHandlers, searchStateHandlers, gridStateHandlers, unitInitLU } = useU3141Context();

  // Form Handlers and default methods
  const validateForm = (landRegister: U3141MasterLotChain) => {
    const result: Record<string, string> = {};
    if (!landRegister.projectNumber) result.projectNumber = t("errProjectNumberRequired", { ns: NS });
    if (!landRegister.projectDescription) result.projectDescription = t("errProjectDescriptionRequired", { ns: NS });
    if (!landRegister.requestedLot) result.requestedLot = t("errRequestedLotRequired", { ns: NS });

    return result;
  };

  const u3141LandRegisterBL: U2000FormSBusinessHandlers<U3141MasterLotChain> = {
    newRecord: () => {
      return U3141EmptyLandRegister;
    },

    // validation du formulaire
    validateForm: validateForm,

    // sauvegarde du formulaire
    saveForme: async (landRegister: U3141MasterLotChain, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(landRegister);
      if (Object.keys(errors).length === 0) {
        handleSave.saveData(landRegister, editMode);
      } else {
        u3141MasterLotChainFormStateExtendedHandlers.setErrors(errors);
      }
    },

    // annulation du formulaire
    cancelForm: () => {
      const landRegister = u3141MasterLotChainFormStateExtendedHandlers.getOldData() as U3141MasterLotChain | null;
      if (landRegister) {
        // // Met à false checkedId pour chaque action
        if (Array.isArray(landRegister.childLotChains)) {
          landRegister.childLotChains = landRegister.childLotChains.map((childLotChain) => ({ ...childLotChain, idChecked: false }));
        }

        u3141MasterLotChainFormStateHandlers.setData({ ...landRegister });
      }

      if (luStateHandlers.getActionOriginFrom() === "/U3141" || luStateHandlers.getActionOriginFrom()?.startsWith("/U3121")) {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },

    // gestion du changement d'enregistrement
    onChangeRecordForm: (logicalUnit) => {
      if (logicalUnit.id) {
        handleGet.getData(logicalUnit.id);
      }
    },

    // gestion du changement de page
    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },

    // gestion de la sortie du formulaire
    onLeaveForm: () => {
      navigate("/U3141");
    },
  };

  const u3141MasterLotChainFormStateHandlers = useU2000FormStateHandlers<U3141MasterLotChain>({
    formState,
    formStateDispatch,
    businessHandlers: u3141LandRegisterBL,
  });

  // extended methods
  const u3141MasterLotChainFormStateExtendedHandlers: U3141MasterLotChainFormExtendedHandlers = {
    ...u3141MasterLotChainFormStateHandlers,

    addNewMasterLotChain: () => {
      u3141MasterLotChainFormStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    },

    modifyMasterLotChain: () => {
      u3141MasterLotChainFormStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },

    deleteMasterLotChain: () => {
      handleDelete.handleDelete();
    },
    fetchHistoriqueCadastral: (masterLotChainId: string) => {
      const data = u3141MasterLotChainFormStateHandlers.getData();
      const telechargerDisabled = !data?.hasHistoriqueCadastralSauvegarde;
      if (!telechargerDisabled) {
        return handleFetchHistoriqueCadastral.fetchHistoriqueCadastral(masterLotChainId);
      }
      setPendingFetchHistoriqueCadastralId(masterLotChainId);
      setFetchHistoriqueCadastralConfirmOpen(true);
      return Promise.resolve();
    },
    downloadHistoriqueCadastralHtml: async (masterLotChainId: string) => {
      // BL-0472 : pdf-file (NAS/cache) si documentId connu ; sinon fallback signUrl S3.
      const data = u3141MasterLotChainFormStateHandlers.getData();
      const documentId = u3141GetHistoriqueCadastralDocumentId(data);
      if (documentId) {
        const payload = await loadDocumentPdfFile(documentId);
        if (payload) {
          const mime = u3141InferDocumentMime(payload.buffer, payload.contentType);
          if (u3141IsHtmlMime(mime)) {
            const raw = u3141DecodeDocumentPdfFileAsText(payload);
            setHistoriqueCadastralHtmlSrcDoc(u3141NormalizeHistoriqueCadastralHtml(raw));
            setHistoriqueCadastralHtmlContentUrl(null);
            setHistoriqueCadastralHtmlOpen(true);
            return;
          }
          u3141OpenDocumentPdfFileInNewTab(payload);
          return;
        }
      }
      return handleDownloadHistoriqueCadastralHtml.downloadHistoriqueCadastralHtml(masterLotChainId);
    },
    mintPlanUploadToken: (masterLotChainId: string) => {
      setPlanUploadClientRequestUuidDraft("");
      setPlanUploadTokenRequest({ masterLotChainId });
      return Promise.resolve();
    },
    downloadSiteAppliSFoncier: async () => {
      if (!unitInitLU.siteAppliSFoncierVersionUrl) {
        toast.error(t("msgDownloadSiteAppliSFoncierError", { ns: NS }), t("errorTitle", { ns: NS }));
        return;
      }
      await handleGetSiteAppliVersion.getSiteAppliSFoncierVersion();
    },
    openReconstituteChainFromPdf: () => {
      setReconstituteChainPdfOpen(true);
    },
    openImportChainAnnotations: () => {
      setImportChainAnnotationsDryRunLots(null);
      setImportChainAnnotationsSourceDocumentId(null);
      setImportChainAnnotationsOpen(true);
    },
    openGenerateAnnotatedLotChain: (anchorChildLotChainId: string) => {
      setGenerateAnnotatedDryRunResult(null);
      setGenerateAnnotatedAnchorChildId(anchorChildLotChainId);
      setGenerateAnnotatedChainOpen(true);
    },
    fetchHistoriqueCadastralCooldown: isFetchHistoriqueCadastralCooldown,
  };

  // Call Search Handlers for Logical Unit
  const handleSearch = useU2000SearchHandlers<U3141MasterLotChain>({
    doSearchFn: (searchPayload: U2000SearchPayload) => {
      return doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3141MasterLotChain>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  /** BL-0711 — GET maître sans laisser la fiche dirty (refetch graphe / WS) ni bloquer la navigation. */
  const applyMasterLotChainFromServerGet = useCallback(
    (masterLotChain: U3141MasterLotChain & { id: string }) => {
      gridStateHandlers.setDataById(masterLotChain.id, masterLotChain);
      const handlers = u3141MasterLotChainFormStateExtendedHandlers;
      const inEdit = handlers.getFormState() === U2000FormStateEnumState.EDIT;
      const userDirty = u2000IsFormRowDirty(handlers.getData(), handlers.getOldData());
      if (!(inEdit && userDirty)) {
        handlers.syncDataFromServer(masterLotChain);
      }
      handlers.runAfterFormCommitNavigation(() => {
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(masterLotChain.id));
        if (newPath !== location.pathname) {
          navigate(newPath, { replace: true });
        }
        setLogicalUnitBreadcrumb([
          { href: "/U3141", label: t("title", { ns: NS }) },
          { label: `${masterLotChain.projectDescription} - (${masterLotChain.projectNumber})` },
        ]);
      });
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        handlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    [
      gridStateHandlers,
      location.pathname,
      luStateHandlers,
      navigate,
      setLogicalUnitBreadcrumb,
      t,
      u3141MasterLotChainFormStateExtendedHandlers,
    ],
  );

  // Call Get Handlers for Logical Unit
  const handleGet = useU2000GetHandlers<U3141MasterLotChain & { id: string }>({
    doGetFn: (id: string) => {
      return doGetData(u2000AxiosAuthenticated, id);
    },
    onSuccess: applyMasterLotChainFromServerGet,
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  (u3141MasterLotChainFormStateExtendedHandlers as U3141MasterLotChainFormExtendedHandlers).refetchMasterLotChain = async () => {
    const data = u3141MasterLotChainFormStateHandlers.getData();
    if (!data?.id) {
      return;
    }
    try {
      const response = await doGetData(u2000AxiosAuthenticated, data.id);
      applyMasterLotChainFromServerGet(response.data as U3141MasterLotChain & { id: string });
    } catch (error: unknown) {
      const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    }
  };

  // Call Save Handlers for Logical Unit
  const handleSave = useU2000SaveHandlers<U3141MasterLotChain>({
    doSaveFn: (landRegister: U3141MasterLotChain, editMode: U2000FormStateEnumEditMode) => {
      const payload = u3141MasterLotChainToApiPayload(landRegister);
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateData(u2000AxiosAuthenticated, payload);
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateData(u2000AxiosAuthenticated, landRegister.id, payload);
      }
      // Ensure a Promise is always returned to satisfy the handler's expected signature
      return Promise.resolve(undefined);
    },
    onSuccess: (landRegister: U3141MasterLotChain & { id: string }, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), landRegister]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridStateHandlers.setDataById(landRegister.id, landRegister);
      }
      // BL-0711 — repasser en lecture avant toute navigation (sinon useBlocker BL-0635 intercepte /new → /:id).
      u3141MasterLotChainFormStateExtendedHandlers.afterSaveData(landRegister);
      toast.success(t("msgLogicalUnitSaved", { ns: NS }));

      u3141MasterLotChainFormStateExtendedHandlers.runAfterFormCommitNavigation(() => {
        if (editMode === U2000FormStateEnumEditMode.ADD) {
          const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(landRegister.id));
          const priorNavState = (location.state ?? {}) as U3141LandRegisterLocationState;
          // BL-0710 — même ouverture auto du graphe Historique cadastral qu’après création depuis U3121.
          navigate(newPath, {
            replace: true,
            state: { ...priorNavState, openLotChainGraph: true },
          });
        }
        if (
          luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY &&
          luStateHandlers.getActionOriginFrom() === "/U3141"
        ) {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      });

      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD) {
        luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
        handleGet.getData(landRegister.id);
      }
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);

      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u3141MasterLotChainFormStateExtendedHandlers.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u3141MasterLotChainFormStateExtendedHandlers.setErrors(errorResult.fieldErrors);
      }
    },
  });

  // Call Delete Handlers for Logical Unit
  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => [u3141MasterLotChainFormStateExtendedHandlers.getData()],
    doDeleteFn: async (id, row) => {
      return await doDeleteData(u2000AxiosAuthenticated, id, row);
    },

    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      let currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t("success0003", { key: row["projectNumber"], ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      // Apres suppression, aller au prochain record, aller vers l'avant si possible, sinon aller vers l'arrière, si aucun, retourner à la liste
      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.PREVIOUSPAGE);
      } else if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.NEXTPAGE);
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate("/U3141", { replace: true });
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  // Fetch Historique Cadastral (job asynchrone)
  const handleFetchHistoriqueCadastral = useU3141UseFetchHistoriqueCadastralHandlers({
    doFetchHistoriqueCadastralFn: (masterLotChainId: string) => doFetchHistoriqueCadastral(u2000AxiosAuthenticated, masterLotChainId),
    onSuccess: (jobId) => {
      if (!jobId) {
        toast.success(t("msgFetchHistoriqueCadastralPending", { ns: NS }));
        return;
      }
      historiqueFinishedRef.current = false;
      setHistoriqueError(null);
      setHistoriqueProgress({ percent: 0, step: "preparation" });
      setHistoriqueJobId(jobId);
    },
    onError: ({ error }) => {
      const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Download Historique Cadastral HTML (fallback S3 signUrl)
  const handleHistoriquePdfFileError = useCallback(
    ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if ((error as { response?: { status?: number } })?.response?.status === 404) {
        toast.error(t("msgNoCadastralHistoryDocument", { ns: NS }), t("errorTitle", { ns: NS }));
      } else if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
    [t, toast]
  );

  const { loadDocumentPdfFile } = useU3141OpenDocumentPdfFileHandlers({
    axios: u2000AxiosAuthenticated,
    onError: handleHistoriquePdfFileError,
  });

  const handleDownloadHistoriqueCadastralHtml = useU3141UseDownloadHistoriqueCadastralHtmlHandlers({
    doGetCadastralHistoryDocumentUrlFn: (masterLotChainId: string) => doGetCadastralHistoryDocumentUrl(u2000AxiosAuthenticated, masterLotChainId),
    onSuccess: async (signUrl) => {
      if (!isHtmlDocumentUrl(signUrl)) {
        window.open(signUrl, "_blank", "noopener,noreferrer");
        return;
      }
      try {
        const res = await fetch(signUrl, { mode: 'cors', credentials: 'omit' });
        if (res.ok) {
          const raw = await res.text();
          const normalized = u3141NormalizeHistoriqueCadastralHtml(raw);
          setHistoriqueCadastralHtmlSrcDoc(normalized);
          setHistoriqueCadastralHtmlContentUrl(null);
          setHistoriqueCadastralHtmlOpen(true);
          return;
        }
      } catch {
        /* repli iframe si CORS ou erreur réseau */
      }
      setHistoriqueCadastralHtmlSrcDoc(null);
      setHistoriqueCadastralHtmlContentUrl(signUrl);
      setHistoriqueCadastralHtmlOpen(true);
    },
    onError: handleHistoriquePdfFileError,
  });

  // Mint jeton upload plans ZIP (programme externe)
  const handleMintPlanUploadToken = useU3141MintPlanUploadTokenHandlers({
    doMintPlanUploadTokenFn: (masterLotChainId, body) =>
      doMintPlanUploadToken(u2000AxiosAuthenticated, masterLotChainId, body) as unknown as Promise<{ data: Record<string, unknown> }>,
    onSuccess: (payload) => {
      if (id) {
        setPlanUploadWatch({ clientRequestUuid: payload.clientRequestUuid, masterLotChainId: id });
      }
      setPlanUploadTokenDialog(payload);
      toast.success(t("msgMintPlanUploadTokenSuccess", { ns: NS }));
    },
    onError: ({ error }) => {
      const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (error instanceof Error && error.message) {
        toast.error(error.message, t("errorTitle", { ns: NS }));
      }
    },
  });

  // Manifeste siteAppliSFoncier via proxy backend (BL-0456 — évite CORS CloudFront)
  const handleGetSiteAppliVersion = useU3141GetSiteAppliSFoncierVersionHandlers({
    doGetSiteAppliSFoncierVersionFn: () => doGetSiteAppliSFoncierVersion(u2000AxiosAuthenticated),
    onSuccess: (versionJson) => {
      const platform = u3141DetectSiteAppliPlatform();
      if (!platform) {
        const choices = u3141ListSiteAppliDownloadChoices(versionJson);
        if (!choices.macos && !choices.windows) {
          toast.error(t("msgDownloadSiteAppliSFoncierError", { ns: NS }), t("errorTitle", { ns: NS }));
          return;
        }
        setSiteAppliOsChoices(choices);
        return;
      }
      const resolved = u3141ResolveSiteAppliArtifact(versionJson, platform);
      if (!resolved.ok) {
        toast.error(t("msgDownloadSiteAppliSFoncierError", { ns: NS }), t("errorTitle", { ns: NS }));
        return;
      }
      window.open(resolved.artifact.url, "_blank", "noopener,noreferrer");
    },
    onError: ({ error }) => {
      const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else {
        toast.error(t("msgDownloadSiteAppliSFoncierError", { ns: NS }), t("errorTitle", { ns: NS }));
      }
    },
  });

  const finishHistorique = useCallback(
    (masterLotChainId: string) => {
      if (historiqueFinishedRef.current) return;
      historiqueFinishedRef.current = true;
      setHistoriqueJobId(null);
      setHistoriqueError(null);
      if (u3141MasterLotChainFormStateExtendedHandlers.getFormState() === U2000FormStateEnumState.READ) {
        handleGet.getData(masterLotChainId);
      }
      void u3141MasterLotChainFormStateExtendedHandlers.refreshLotChainGraphIfOpen?.({ relayoutStructure: true });
    },
    [handleGet, u3141MasterLotChainFormStateExtendedHandlers]
  );
  const finishHistoriqueRef = useRef(finishHistorique);
  finishHistoriqueRef.current = finishHistorique;

  const handlePlanUploadWsProgress = useCallback(
    (payload: U3141PlanUploadProgressWsMessage) => {
      if (payload.phase === "started") {
        setPlanUploadTokenDialog(null);
      }
      setPlanUploadProgress((prev) => u3141ApplyPlanUploadProgressEvent(prev, payload));
      if (payload.phase === "done") {
        if (payload.anySuccess && payload.errorCount === 0) {
          toast.success(t("msgPlanUploadProgressDoneSuccess", { ns: NS }));
        } else if (payload.anySuccess) {
          toast.success(t("msgPlanUploadProgressDonePartial", { ns: NS, errorCount: payload.errorCount }));
        } else {
          toast.error(t("msgPlanUploadProgressDoneFailed", { ns: NS }), t("errorTitle", { ns: NS }));
        }
        if (
          id &&
          payload.masterLotChainId === id &&
          u3141MasterLotChainFormStateExtendedHandlers.getFormState() === U2000FormStateEnumState.READ
        ) {
          handleGet.getData(id);
        }
      }
      if (payload.phase === "failed") {
        toast.error(payload.message, t("errorTitle", { ns: NS }));
      }
    },
    [handleGet, id, t, toast, u3141MasterLotChainFormStateExtendedHandlers]
  );

  useU3141PlanUploadProgressNotification({
    watchContext: {
      clientRequestUuid:
        planUploadWatch && planUploadWatch.masterLotChainId === id
          ? planUploadWatch.clientRequestUuid
          : null,
      masterLotChainId: id,
    },
    onProgress: handlePlanUploadWsProgress,
  });

  // Notification WebSocket : en succès, ferme le dialogue et rafraîchit ; en échec le poll garde le message.
  useU3141HistoriqueCadastralNotification(id ?? undefined, (masterLotChainId) => {
    if (historiqueErrorRef.current) return;
    finishHistorique(masterLotChainId);
  });

  // Import PDF de chaîne (BL-0562) — job asynchrone
  const handleReconstituteChainFromPdf = useU3141UseReconstituteChainFromPdfHandlers({
    doReconstituteChainFromPdfFn: (masterLotChainId, file) =>
      doReconstituteChainFromPdf(u2000AxiosAuthenticated, masterLotChainId, file),
    onSuccess: (jobId) => {
      setReconstituteChainPdfSubmitting(false);
      setReconstituteChainPdfOpen(false);
      if (!jobId) {
        toast.success(t("msgReconstituteChainPending", { ns: NS }));
        return;
      }
      chainImportFinishedRef.current = false;
      pendingReconstituteJobIdRef.current = jobId;
      setChainImportError(null);
      setChainImportProgress({ percent: 0, step: "preparation" });
      setChainImportJobId(jobId);
    },
    onError: ({ error }) => {
      setReconstituteChainPdfSubmitting(false);
      const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const loadChainPdfAlertesFromJob = useCallback(
    async (jobId: string, ficheCount?: number) => {
      try {
        const response = await doGetU2200Job(u2000AxiosAuthenticated, jobId);
        const job = u3141ParseU2200JobResponse(response.data);
        const alertes = u3141ExtractChainPdfAlertesFromJob(job);
        const countFromJob = job?.result?.data?.ficheCount;
        setChainPdfAlertes(alertes);
        setChainPdfAlertesFicheCount(
          typeof countFromJob === "number" ? countFromJob : ficheCount
        );
        setChainPdfAlertesOpen(true);
      } catch {
        // Pas de droit U2200:READ ou job inaccessible — le toast notification suffit.
        toast.success(
          t("msgReconstituteChainCompletedWithAlertes", {
            ns: NS,
            alerteCount: "?",
          })
        );
      }
    },
    [u2000AxiosAuthenticated, t, toast]
  );

  const notifyLotsRegeneres = useCallback(
    (lots: U3141ChainPdfLotRegenere[]) => {
      if (lots.length === 0) return;
      const newShapeCount = lots.reduce((sum, row) => sum + row.newShapeCount, 0);
      toast.success(
        t("msgChainPdfAnnotationsRegenerated", {
          ns: NS,
          lotCount: lots.length,
          newShapeCount,
          lots: lots.map((row) => row.lot).join(", "),
        })
      );
    },
    [t, toast]
  );

  const finishChainImport = useCallback(
    (payload: {
      masterLotChainId: string;
      alerteCount: number;
      ficheCount?: number;
      lotsRegeneres?: U3141ChainPdfLotRegenere[];
    }) => {
      if (chainImportFinishedRef.current) return;
      chainImportFinishedRef.current = true;
      const jobId = pendingReconstituteJobIdRef.current;
      setChainImportJobId(null);
      setChainImportError(null);
      pendingReconstituteJobIdRef.current = null;
      if (u3141MasterLotChainFormStateExtendedHandlers.getFormState() === U2000FormStateEnumState.READ) {
        handleGet.getData(payload.masterLotChainId);
      }
      if (payload.lotsRegeneres) {
        notifyLotsRegeneres(payload.lotsRegeneres);
      } else if (jobId) {
        void doGetChainImportJob(u2000AxiosAuthenticated, jobId)
          .then((response) => {
            const job = u3141ParseU2200JobResponse(response.data);
            notifyLotsRegeneres(u3141ExtractChainPdfLotsRegeneresFromJob(job));
          })
          .catch(() => {
            // Le résumé annotations reste absent si le job n'est plus lisible.
          });
      }
      if (payload.alerteCount > 0 && jobId) {
        void loadChainPdfAlertesFromJob(jobId, payload.ficheCount);
      } else if (payload.alerteCount > 0) {
        toast.success(
          t("msgReconstituteChainCompletedWithAlertes", {
            ns: NS,
            alerteCount: payload.alerteCount,
          })
        );
      }
    },
    [
      handleGet,
      loadChainPdfAlertesFromJob,
      notifyLotsRegeneres,
      t,
      toast,
      u2000AxiosAuthenticated,
      u3141MasterLotChainFormStateExtendedHandlers,
    ]
  );

  const finishChainImportRef = useRef(finishChainImport);
  finishChainImportRef.current = finishChainImport;

  useU3141ReconstituteChainFromPdfNotification(id ?? undefined, (payload) => {
    if (payload.failed) {
      setChainImportError(payload.message || t("msgReconstituteChainFailed", { ns: NS }));
      return;
    }
    finishChainImport(payload);
  });

  const resetImportChainAnnotationsWizard = useCallback(() => {
    setImportChainAnnotationsDryRunLots(null);
    setImportChainAnnotationsSourceDocumentId(null);
  }, []);

  const handleImportChainAnnotations = useU3141ImportChainAnnotationsHandlers({
    doDryRunFn: (masterLotChainId, file) =>
      doImportChainAnnotationsDryRun(u2000AxiosAuthenticated, masterLotChainId, file),
    doApplyFn: (masterLotChainId, body) =>
      doImportChainAnnotationsApply(u2000AxiosAuthenticated, masterLotChainId, body),
    onDryRunSuccess: (result) => {
      setImportChainAnnotationsDryRunLoading(false);
      setImportChainAnnotationsDryRunLots(result.lots);
      setImportChainAnnotationsSourceDocumentId(result.sourceDocumentId);
    },
    onApplySuccess: (jobId) => {
      setImportChainAnnotationsApplyLoading(false);
      setImportChainAnnotationsOpen(false);
      resetImportChainAnnotationsWizard();
      if (!jobId) {
        toast.success(t("msgImportChainAnnotationsPending", { ns: NS }));
        return;
      }
      importChainAnnotationsFinishedRef.current = false;
      pendingImportChainAnnotationsJobIdRef.current = jobId;
      setImportChainAnnotationsError(null);
      setImportChainAnnotationsProgress({ percent: 0, step: "preparation" });
      setImportChainAnnotationsJobId(jobId);
    },
    onError: ({ error }) => {
      setImportChainAnnotationsDryRunLoading(false);
      setImportChainAnnotationsApplyLoading(false);
      const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const resetGenerateAnnotatedWizard = useCallback(() => {
    setGenerateAnnotatedDryRunResult(null);
    setGenerateAnnotatedAnchorChildId(null);
  }, []);

  const handleGenerateAnnotatedLotChain = useU3141GenerateAnnotatedLotChainHandlers({
    doDryRunFn: (masterLotChainId, anchorChildLotChainId) =>
      doFetchAnnotatedLotChainIndexPdfDryRun(u2000AxiosAuthenticated, masterLotChainId, anchorChildLotChainId),
    doLaunchMergeFn: (masterLotChainId, anchorChildLotChainId) =>
      doFetchAnnotatedLotChainIndexPdf(u2000AxiosAuthenticated, masterLotChainId, anchorChildLotChainId),
    onDryRunSuccess: (result) => {
      setGenerateAnnotatedDryRunLoading(false);
      setGenerateAnnotatedDryRunResult(result);
    },
    onLaunchSuccess: (jobId) => {
      setGenerateAnnotatedLaunchLoading(false);
      const anchorId = generateAnnotatedAnchorChildId;
      if (anchorId) {
        u3141MasterLotChainFormStateExtendedHandlers.beforeAnnotatedMergeJobLaunch?.(anchorId);
      }
      setGenerateAnnotatedChainOpen(false);
      resetGenerateAnnotatedWizard();
      toast.success(t("msgAnnotatedLotChainPending", { ns: NS }), t("successTitle", { ns: NS }));
      annotatedMergeFinishedRef.current = false;
      pendingAnnotatedMergeJobIdRef.current = jobId || null;
      setAnnotatedMergeError(null);
      setAnnotatedMergeProgress({ percent: 0, step: "preparation" });
      setAnnotatedMergeJobId(jobId || null);
    },
    onError: ({ error }) => {
      setGenerateAnnotatedDryRunLoading(false);
      setGenerateAnnotatedLaunchLoading(false);
      const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const runGenerateAnnotatedDryRun = useCallback(async () => {
    const masterId = id ?? u3141MasterLotChainFormStateHandlers.getData()?.id;
    const anchorId = generateAnnotatedAnchorChildId;
    if (!masterId || !anchorId || generateAnnotatedDryRunLoading) return;
    setGenerateAnnotatedDryRunLoading(true);
    setGenerateAnnotatedDryRunResult(null);
    await handleGenerateAnnotatedLotChain.dryRunAnnotatedLotChainMerge(masterId, anchorId);
  }, [
    generateAnnotatedAnchorChildId,
    generateAnnotatedDryRunLoading,
    handleGenerateAnnotatedLotChain,
    id,
    u3141MasterLotChainFormStateHandlers,
  ]);

  useEffect(() => {
    if (!isGenerateAnnotatedChainOpen || !generateAnnotatedAnchorChildId) return;
    void runGenerateAnnotatedDryRun();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dry-run au premier open uniquement
  }, [isGenerateAnnotatedChainOpen, generateAnnotatedAnchorChildId]);

  const finishAnnotatedLotChainMerge = useCallback(
    (masterLotChainId: string) => {
      if (annotatedMergeFinishedRef.current) return;
      annotatedMergeFinishedRef.current = true;
      pendingAnnotatedMergeJobIdRef.current = null;
      setAnnotatedMergeJobId(null);
      setAnnotatedMergeError(null);
      if (u3141MasterLotChainFormStateExtendedHandlers.getFormState() === U2000FormStateEnumState.READ) {
        handleGet.getData(masterLotChainId);
      }
      void u3141MasterLotChainFormStateExtendedHandlers.refreshLotChainGraphIfOpen?.();
      toast.success(t("msgAnnotatedLotChainCompleted", { ns: NS }), t("successTitle", { ns: NS }));
    },
    [handleGet, t, toast, u3141MasterLotChainFormStateExtendedHandlers],
  );

  const finishAnnotatedLotChainMergeRef = useRef(finishAnnotatedLotChainMerge);
  finishAnnotatedLotChainMergeRef.current = finishAnnotatedLotChainMerge;

  useU3141AnnotatedLotChainNotification(id ?? undefined, (payload) => {
    if (payload.failed) {
      setAnnotatedMergeError(payload.message || t("msgAnnotatedLotChainFailed", { ns: NS }));
      return;
    }
    if (annotatedMergeJobId) {
      finishAnnotatedLotChainMergeRef.current(payload.masterLotChainId);
    }
  });

  useEffect(() => {
    if (!annotatedMergeJobId || annotatedMergeError) return;
    let cancelled = false;
    const jobId = annotatedMergeJobId;

    const tick = async () => {
      try {
        const response = await doGetChainImportJob(u2000AxiosAuthenticated, jobId);
        if (cancelled) return;
        const job = u3141ParseU2200JobResponse(response.data);
        if (!job) return;
        setAnnotatedMergeProgress(u3141ReadChainImportProgress(job));
        if (job.status === "failed") {
          setAnnotatedMergeError(job.errorMessage || t("msgAnnotatedLotChainFailed", { ns: NS }));
          return;
        }
        if (job.status === "completed") {
          finishAnnotatedLotChainMergeRef.current(id ?? "");
        }
      } catch {
        // Le prochain poll réessaie.
      }
    };

    void tick();
    const timer = window.setInterval(() => void tick(), 2000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [annotatedMergeJobId, annotatedMergeError, id, t, u2000AxiosAuthenticated]);

  const finishImportChainAnnotations = useCallback(
    (masterLotChainId: string) => {
      if (importChainAnnotationsFinishedRef.current) return;
      importChainAnnotationsFinishedRef.current = true;
      const jobId = pendingImportChainAnnotationsJobIdRef.current;
      setImportChainAnnotationsJobId(null);
      setImportChainAnnotationsError(null);
      pendingImportChainAnnotationsJobIdRef.current = null;
      if (u3141MasterLotChainFormStateExtendedHandlers.getFormState() === U2000FormStateEnumState.READ) {
        handleGet.getData(masterLotChainId);
      }
      void u3141MasterLotChainFormStateExtendedHandlers.refreshLotChainGraphIfOpen?.();
      if (jobId) {
        void doGetChainImportJob(u2000AxiosAuthenticated, jobId)
          .then((response) => {
            const job = u3141ParseU2200JobResponse(response.data);
            const summary = u3141ExtractImportChainAnnotationsSummaryFromJob(job);
            toast.success(
              t("msgImportChainAnnotationsSummary", {
                ns: NS,
                applied: summary.lotsAppliques.length,
                ignored: summary.lotsIgnores.length,
                identicalSkipped: summary.lotsIdentiquesNonDemandes.length,
              }),
            );
          })
          .catch(() => {
            toast.success(t("msgImportChainAnnotationsCompleted", { ns: NS }));
          });
      } else {
        toast.success(t("msgImportChainAnnotationsCompleted", { ns: NS }));
      }
    },
    [
      handleGet,
      t,
      toast,
      u2000AxiosAuthenticated,
      u3141MasterLotChainFormStateExtendedHandlers,
    ],
  );

  const finishImportChainAnnotationsRef = useRef(finishImportChainAnnotations);
  finishImportChainAnnotationsRef.current = finishImportChainAnnotations;

  useU3141ImportChainAnnotationsNotification(id ?? undefined, (payload) => {
    if (payload.failed) {
      setImportChainAnnotationsError(payload.message || t("msgImportChainAnnotationsFailed", { ns: NS }));
      return;
    }
    finishImportChainAnnotationsRef.current(payload.masterLotChainId);
  });

  useEffect(() => {
    if (!importChainAnnotationsJobId || importChainAnnotationsError) return;
    let cancelled = false;
    const jobId = importChainAnnotationsJobId;

    const tick = async () => {
      try {
        const response = await doGetChainImportJob(u2000AxiosAuthenticated, jobId);
        if (cancelled) return;
        const job = u3141ParseU2200JobResponse(response.data);
        if (!job) return;
        setImportChainAnnotationsProgress(u3141ReadChainImportProgress(job));
        if (job.status === "failed") {
          setImportChainAnnotationsError(job.errorMessage || t("msgImportChainAnnotationsFailed", { ns: NS }));
          return;
        }
        if (job.status === "completed") {
          finishImportChainAnnotationsRef.current(id ?? "");
        }
      } catch {
        // Le prochain poll réessaie.
      }
    };

    void tick();
    const timer = window.setInterval(() => void tick(), 2000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [importChainAnnotationsJobId, importChainAnnotationsError, id, t, u2000AxiosAuthenticated]);

  useEffect(() => {
    if (!chainImportJobId || chainImportError) return;
    let cancelled = false;
    const jobId = chainImportJobId;

    const tick = async () => {
      try {
        const response = await doGetChainImportJob(u2000AxiosAuthenticated, jobId);
        if (cancelled) return;
        const job = u3141ParseU2200JobResponse(response.data);
        if (!job) return;
        setChainImportProgress(u3141ReadChainImportProgress(job));
        if (job.status === "failed") {
          setChainImportError(job.errorMessage || t("msgReconstituteChainFailed", { ns: NS }));
          return;
        }
        if (job.status === "completed") {
          const data = job.result?.data;
          finishChainImportRef.current({
            masterLotChainId: id ?? "",
            alerteCount: typeof data?.alerteCount === "number" ? data.alerteCount : 0,
            ficheCount: typeof data?.ficheCount === "number" ? data.ficheCount : undefined,
            lotsRegeneres: u3141ExtractChainPdfLotsRegeneresFromJob(job),
          });
        }
      } catch {
        // Le prochain poll réessaie. Le dialogue garde la dernière étape connue.
      }
    };

    void tick();
    const timer = window.setInterval(() => void tick(), 2000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [chainImportJobId, chainImportError, id, t, u2000AxiosAuthenticated]);

  useEffect(() => {
    if (!historiqueJobId || historiqueError) return;
    let cancelled = false;
    const jobId = historiqueJobId;

    const tick = async () => {
      try {
        const response = await doGetChainImportJob(u2000AxiosAuthenticated, jobId);
        if (cancelled) return;
        const job = u3141ParseU2200JobResponse(response.data);
        if (!job) return;
        setHistoriqueProgress(u3141ReadChainImportProgress(job));
        if (job.status === "failed") {
          setHistoriqueError(job.errorMessage || t("msgFetchHistoriqueCadastralFailed", { ns: NS }));
          return;
        }
        if (job.status === "completed") {
          finishHistoriqueRef.current(id ?? "");
        }
      } catch {
        // Le prochain poll réessaie. Le dialogue garde la dernière étape connue.
      }
    };

    void tick();
    const timer = window.setInterval(() => void tick(), 2000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [historiqueJobId, historiqueError, id, t, u2000AxiosAuthenticated]);

  // Initialisation au montage et quand id change (ex. F5 sur /U3141/:id : id peut arriver après le 1er rendu).
  useEffect(() => {
    if (id === undefined) return;
    if (id === "new") {
      u3141MasterLotChainFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
      const navState = location.state as {
        u3141CreatePrefill?: U3141CreatePrefillState;
        u3141ActionOriginFrom?: string;
      } | null;
      const prefill = navState?.u3141CreatePrefill;
      const origin = navState?.u3141ActionOriginFrom;
      if (origin) {
        luStateHandlers.setActionOriginFrom(origin);
        luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
      }
      if (prefill?.projectNumber?.trim() && prefill?.requestedLot?.trim()) {
        const { projectNumber, projectDescription, requestedLot } = prefill;
        u3141MasterLotChainFormStateExtendedHandlers.setData({
          ...U3141EmptyLandRegister,
          projectNumber,
          projectDescription,
          requestedLot,
          selectedProject: {
            id: projectNumber,
            projetNumber: projectNumber,
            projetName: projectDescription,
          },
          selectedRequestedLot: {
            id: requestedLot,
            requestedLot,
          },
        });
      } else {
        u3141MasterLotChainFormStateExtendedHandlers.setData({ ...U3141EmptyLandRegister });
      }
    } else {
      handleGet.getData(id);
    }
  }, [id]);

  // Tabs du formulaire
  const formTabsComponent: any[] = [
    {
      id: "tabChildLotChain",
      label: t("tabChildLotChains", { ns: NS }),
      content: <U3141MasterChildLotChainBody u3141MasterLotChainFormHandlers={u3141MasterLotChainFormStateExtendedHandlers} />,
    },
    hasRole(U3141ROLES.AUDITS) && {
      id: "tabAudits",
      label: t("tabAudits", { ns: NS }),
      content: <U2000AuditPanelComponent id={u3141MasterLotChainFormStateExtendedHandlers.getData()?.id} doGetAuditsData={doGetAuditsData} toastName="U3141LandRegisterAudit" />,
    },
  ].filter(Boolean);

  const COOLDOWN_MS = 45000;

  const handleFetchHistoriqueCadastralConfirmClose = (result: "yes" | "no") => {
    if (result === "yes" && pendingFetchHistoriqueCadastralId) {
      setFetchHistoriqueCadastralCooldown(true);
      handleFetchHistoriqueCadastral.fetchHistoriqueCadastral(pendingFetchHistoriqueCadastralId);
      setTimeout(() => setFetchHistoriqueCadastralCooldown(false), COOLDOWN_MS);
    }
    setFetchHistoriqueCadastralConfirmOpen(false);
    setPendingFetchHistoriqueCadastralId(null);
  };

  const handleHistoriqueCadastralHtmlClose = () => {
    setHistoriqueCadastralHtmlOpen(false);
    setHistoriqueCadastralHtmlContentUrl(null);
    setHistoriqueCadastralHtmlSrcDoc(null);
  };

  const handlePlanUploadTokenDialogClose = () => {
    setPlanUploadTokenDialog(null);
  };

  const handlePlanUploadProgressClose = () => {
    setPlanUploadProgress((prev) => ({ ...prev, open: false }));
  };

  const handlePlanUploadTokenRequestClose = () => {
    setPlanUploadTokenRequest(null);
    setPlanUploadClientRequestUuidDraft("");
  };

  const handlePlanUploadTokenRequestSubmit = () => {
    if (!planUploadTokenRequest) return;
    const trimmed = planUploadClientRequestUuidDraft.trim();
    if (!u3141IsClientRequestUuid(trimmed)) {
      toast.error(t("errMintPlanUploadTokenClientUuidInvalid", { ns: NS }), t("errorTitle", { ns: NS }));
      return;
    }
    void handleMintPlanUploadToken.mintPlanUploadToken(planUploadTokenRequest.masterLotChainId, trimmed);
    handlePlanUploadTokenRequestClose();
  };

  const handleCopyPlanUploadToken = async () => {
    if (!planUploadTokenDialog?.accessToken) return;
    try {
      await navigator.clipboard.writeText(planUploadTokenDialog.accessToken);
      toast.success(t("msgMintPlanUploadTokenCopied", { ns: NS }));
    } catch {
      toast.error(t("msgMintPlanUploadTokenCopyFailed", { ns: NS }), t("errorTitle", { ns: NS }));
    }
  };

  const handleSiteAppliOsDialogClose = () => {
    setSiteAppliOsChoices(null);
  };

  const handleSiteAppliOsDownload = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
    setSiteAppliOsChoices(null);
  };

  const handleReconstituteChainPdfSubmit = async (file: File) => {
    const masterId = u3141MasterLotChainFormStateExtendedHandlers.getData()?.id;
    if (!masterId) return;
    setReconstituteChainPdfSubmitting(true);
    await handleReconstituteChainFromPdf.reconstituteChainFromPdf(masterId, file);
  };

  const masterFormHandlersU2000 = u3141MasterLotChainFormStateExtendedHandlers as unknown as U2000FormStateHandlers<U3141MasterLotChain>;

  // Render
  return (
    <>
      <U2000HtmlDisplayDialog
        isOpen={isHistoriqueCadastralHtmlOpen}
        onClose={handleHistoriqueCadastralHtmlClose}
        title={t("dlgHistoriqueCadastralHtmlTitle", { ns: NS })}
        contentUrl={historiqueCadastralHtmlContentUrl}
        htmlContent={historiqueCadastralHtmlSrcDoc}
      />
      <U2000YesNoDialog isOpen={isFetchHistoriqueCadastralConfirmOpen} onClose={handleFetchHistoriqueCadastralConfirmClose} title={t("dlgFetchHistoriqueCadastralTitle", { ns: NS })}>
        <p>{t("dlgFetchHistoriqueCadastralMessage", { ns: NS })}</p>
      </U2000YesNoDialog>
      <U3141ReconstituteChainPdfDialog
        isOpen={isReconstituteChainPdfOpen}
        onClose={() => {
          if (!isReconstituteChainPdfSubmitting) setReconstituteChainPdfOpen(false);
        }}
        onSubmit={handleReconstituteChainPdfSubmit}
        submitting={isReconstituteChainPdfSubmitting}
      />
      <U3141GenerateAnnotatedLotChainDialog
        isOpen={isGenerateAnnotatedChainOpen}
        onClose={() => {
          if (!generateAnnotatedDryRunLoading && !generateAnnotatedLaunchLoading) {
            setGenerateAnnotatedChainOpen(false);
            resetGenerateAnnotatedWizard();
          }
        }}
        dryRunResult={generateAnnotatedDryRunResult}
        dryRunLoading={generateAnnotatedDryRunLoading}
        launchLoading={generateAnnotatedLaunchLoading}
        onGenerate={async () => {
          const masterId = id ?? u3141MasterLotChainFormStateHandlers.getData()?.id;
          const anchorId = generateAnnotatedAnchorChildId;
          if (
            !masterId ||
            !anchorId ||
            !u3141CanLaunchAnnotatedLotChainMergeFromDryRun(generateAnnotatedDryRunResult?.summary)
          ) {
            return;
          }
          setGenerateAnnotatedLaunchLoading(true);
          await handleGenerateAnnotatedLotChain.launchAnnotatedLotChainMerge(masterId, anchorId);
        }}
      />
      <U3141ChainImportProgressDialog
        isOpen={annotatedMergeJobId !== null}
        percent={annotatedMergeProgress.percent}
        step={annotatedMergeProgress.step}
        current={annotatedMergeProgress.current}
        total={annotatedMergeProgress.total}
        detail={annotatedMergeProgress.detail}
        errorMessage={annotatedMergeError}
        steps={U3141_ANNOTATED_LOT_CHAIN_MERGE_STEPS}
        titleKey="dlgAnnotatedMergeProgressTitle"
        introKey="dlgAnnotatedMergeProgressIntro"
        closeHintKey="dlgChainImportProgressCloseHint"
        onClose={() => {
          setAnnotatedMergeJobId(null);
          setAnnotatedMergeError(null);
        }}
      />
      <U3141ImportChainAnnotationsDialog
        isOpen={isImportChainAnnotationsOpen}
        onClose={() => {
          if (!importChainAnnotationsDryRunLoading && !importChainAnnotationsApplyLoading) {
            setImportChainAnnotationsOpen(false);
            resetImportChainAnnotationsWizard();
          }
        }}
        masterLotChainId={id}
        dryRunLots={importChainAnnotationsDryRunLots}
        dryRunSourceDocumentId={importChainAnnotationsSourceDocumentId}
        dryRunLoading={importChainAnnotationsDryRunLoading}
        applyLoading={importChainAnnotationsApplyLoading}
        onDryRun={async (file) => {
          const masterId = u3141MasterLotChainFormStateExtendedHandlers.getData()?.id;
          if (!masterId) return;
          setImportChainAnnotationsDryRunLoading(true);
          setImportChainAnnotationsDryRunLots(null);
          setImportChainAnnotationsSourceDocumentId(null);
          await handleImportChainAnnotations.dryRunImportChainAnnotations(masterId, file);
        }}
        onApply={async (sourceDocumentId, lotDetailIdsToReplace) => {
          const masterId = u3141MasterLotChainFormStateExtendedHandlers.getData()?.id;
          if (!masterId) return;
          setImportChainAnnotationsApplyLoading(true);
          await handleImportChainAnnotations.applyImportChainAnnotations(masterId, {
            sourceDocumentId,
            lotDetailIdsToReplace,
          });
        }}
      />
      <U3141ChainImportProgressDialog
        isOpen={importChainAnnotationsJobId !== null}
        percent={importChainAnnotationsProgress.percent}
        step={importChainAnnotationsProgress.step}
        current={importChainAnnotationsProgress.current}
        total={importChainAnnotationsProgress.total}
        detail={importChainAnnotationsProgress.detail}
        errorMessage={importChainAnnotationsError}
        steps={U3141_CHAIN_ANNOTATIONS_IMPORT_STEPS}
        titleKey="dlgImportChainAnnotationsProgressTitle"
        introKey="dlgImportChainAnnotationsProgressIntro"
        closeHintKey="dlgChainImportProgressCloseHint"
        onClose={() => {
          setImportChainAnnotationsJobId(null);
          setImportChainAnnotationsError(null);
        }}
      />
      <U3141ChainImportProgressDialog
        isOpen={chainImportJobId !== null}
        percent={chainImportProgress.percent}
        step={chainImportProgress.step}
        current={chainImportProgress.current}
        total={chainImportProgress.total}
        detail={chainImportProgress.detail}
        errorMessage={chainImportError}
        onClose={() => {
          setChainImportJobId(null);
          setChainImportError(null);
        }}
      />
      <U3141ChainImportProgressDialog
        isOpen={historiqueJobId !== null}
        percent={historiqueProgress.percent}
        step={historiqueProgress.step}
        current={historiqueProgress.current}
        total={historiqueProgress.total}
        detail={historiqueProgress.detail}
        errorMessage={historiqueError}
        steps={U3141_HISTORIQUE_PROGRESS_STEPS}
        titleKey="dlgHistoriqueProgressTitle"
        introKey="dlgHistoriqueProgressIntro"
        closeHintKey="dlgHistoriqueProgressCloseHint"
        stepLabelPrefix="lblHistoriqueProgressStep"
        onClose={() => {
          setHistoriqueJobId(null);
          setHistoriqueError(null);
        }}
      />
      <U3141ChainPdfAlertesDialog
        isOpen={chainPdfAlertesOpen}
        onClose={() => {
          setChainPdfAlertesOpen(false);
          setChainPdfAlertes([]);
          setChainPdfAlertesFicheCount(undefined);
        }}
        alertes={chainPdfAlertes}
        ficheCount={chainPdfAlertesFicheCount}
      />
      <U2000BasicDialog
        isOpen={planUploadTokenRequest !== null}
        onClose={handlePlanUploadTokenRequestClose}
        title={t("dlgMintPlanUploadTokenRequestTitle", { ns: NS })}
        width="medium"
      >
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-700">{t("dlgMintPlanUploadTokenRequestIntro", { ns: NS })}</p>
          <div>
            <label htmlFor="plan-upload-client-request-uuid" className="block text-xs font-medium text-gray-600 mb-1">
              {t("lblMintPlanUploadTokenRequestId", { ns: NS })}
            </label>
            <input
              id="plan-upload-client-request-uuid"
              type="text"
              autoComplete="off"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 font-mono"
              placeholder={t("placeholderMintPlanUploadTokenRequestId", { ns: NS })}
              value={planUploadClientRequestUuidDraft}
              onChange={(e) => setPlanUploadClientRequestUuidDraft(e.target.value)}
            />
            <p className="mt-1 text-xs text-gray-500">{t("hintMintPlanUploadTokenRequestId", { ns: NS })}</p>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <button type="button" className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50" onClick={handlePlanUploadTokenRequestClose}>
              {t("btnMintPlanUploadTokenRequestCancel", { ns: NS })}
            </button>
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700"
              onClick={handlePlanUploadTokenRequestSubmit}
            >
              {t("btnMintPlanUploadTokenRequestSubmit", { ns: NS })}
            </button>
          </div>
        </div>
      </U2000BasicDialog>
      <U3141PlanUploadProgressDialog state={planUploadProgress} onClose={handlePlanUploadProgressClose} />
      <U2000BasicDialog
        isOpen={planUploadTokenDialog !== null}
        onClose={handlePlanUploadTokenDialogClose}
        title={t("dlgMintPlanUploadTokenTitle", { ns: NS })}
        width="large"
      >
        {planUploadTokenDialog ? (
          <div className="px-6 py-4 space-y-4">
            <p className="text-sm text-gray-700">{t("dlgMintPlanUploadTokenIntro", { ns: NS })}</p>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("dlgMintPlanUploadTokenClientUuidLabel", { ns: NS })}</label>
              <pre className="text-xs bg-muted p-2 rounded-lg break-all whitespace-pre-wrap">{planUploadTokenDialog.clientRequestUuid}</pre>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("dlgMintPlanUploadTokenExpiresLabel", { ns: NS })}</label>
              <p className="text-sm">{planUploadTokenDialog.expiresAt || "—"}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("dlgMintPlanUploadTokenAccessTokenLabel", { ns: NS })}</label>
              <pre className="text-xs bg-muted p-2 rounded-lg break-all whitespace-pre-wrap max-h-40 overflow-y-auto">{planUploadTokenDialog.accessToken}</pre>
            </div>
            <p className="text-xs text-gray-600">{t("dlgMintPlanUploadTokenUploadHint", { ns: NS })}</p>
            <div className="flex gap-2 justify-end pt-2">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700"
                onClick={() => void handleCopyPlanUploadToken()}
              >
                {t("btnMintPlanUploadTokenCopy", { ns: NS })}
              </button>
              <button type="button" className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50" onClick={() => handlePlanUploadTokenDialogClose()}>
                {t("btnMintPlanUploadTokenClose", { ns: NS })}
              </button>
            </div>
          </div>
        ) : null}
      </U2000BasicDialog>
      <U2000BasicDialog
        isOpen={siteAppliOsChoices !== null}
        onClose={handleSiteAppliOsDialogClose}
        title={t("dlgDownloadSiteAppliSFoncierOsTitle", { ns: NS })}
        width="medium"
      >
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-700">{t("dlgDownloadSiteAppliSFoncierOsIntro", { ns: NS })}</p>
          <p className="text-xs text-gray-500">{t("hintDownloadSiteAppliSFoncier", { ns: NS })}</p>
          <div className="flex flex-col gap-2">
            {siteAppliOsChoices?.macos?.url ? (
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700"
                onClick={() => handleSiteAppliOsDownload(siteAppliOsChoices.macos!.url)}
              >
                {t("btnDownloadSiteAppliSFoncierMacos", { ns: NS })}
              </button>
            ) : null}
            {siteAppliOsChoices?.windows?.url ? (
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700"
                onClick={() => handleSiteAppliOsDownload(siteAppliOsChoices.windows!.url)}
              >
                {t("btnDownloadSiteAppliSFoncierWindows", { ns: NS })}
              </button>
            ) : null}
          </div>
          <div className="flex justify-end pt-2">
            <button type="button" className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50" onClick={handleSiteAppliOsDialogClose}>
              {t("btnDownloadSiteAppliSFoncierOsCancel", { ns: NS })}
            </button>
          </div>
        </div>
      </U2000BasicDialog>
      <U3141LandRegisterDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2000DefaultForm
        actionsBtns={<U3141MasterLotChainFormToolBar formStateHandlers={u3141MasterLotChainFormStateExtendedHandlers} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={masterFormHandlersU2000}
      >
        <U3141MasterLotChainBody formStateHandlers={masterFormHandlersU2000} t={t} />

        <U2000Tab
          className="mt-4"
          tabs={formTabsComponent}
          defaultTab="tabChildLotChain"
          activeTab={activeTab ?? "tabChildLotChain"}
          setActiveTab={(tab) => setActiveTab(typeof tab === "string" ? tab : String(tab))}
        />
      </U2000DefaultForm>
    </>
  );
};

export default U3141LandRegisterForm;
